import { useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2, Plus, X, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useCapabilityCreate } from '@/hooks/use-entity-mutations';
import { getCapabilityColors } from '@/lib/capability-colors';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { VisibilitySelect, type CapabilityVisibility } from '@/components/ui/visibility-select';
import type { CreatedCapability } from '../CreateCapabilityDrawer';

const TOTAL_STEPS = 2;

const STEP_META = [
    { title: 'Basics', description: 'Name, description, and tags' },
    { title: 'Prompt text', description: 'Define the prompt content' },
] as const;

function slugifyToUnderscore(value: string): string {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9\s_-]/g, '')
        .replace(/[\s-]+/g, '_')
        .replace(/_+/g, '_')
        .replace(/^_+|_+$/g, '');
}

export interface PromptCreateWizardProps {
    appKey: string;
    onClose: () => void;
    onSuccess?: (entity: CreatedCapability) => void;
}

export function PromptCreateWizard({
    appKey,
    onClose,
    onSuccess,
}: PromptCreateWizardProps) {
    const createCapability = useCapabilityCreate(appKey);
    const colors = getCapabilityColors('prompt');
    const TypeIcon = colors.icon;

    const [currentStep, setCurrentStep] = useState(0);
    const [errors, setErrors] = useState<Record<string, string>>({});

    const [name, setName] = useState('');
    const [key, setKey] = useState('');
    const [description, setDescription] = useState('');
    const [content, setContent] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [tagInput, setTagInput] = useState('');
    const [visibility, setVisibility] = useState<CapabilityVisibility>('account');

    const handleNameChange = (value: string) => {
        setName(value);
        setKey(slugifyToUnderscore(value));
    };

    const handleAddTag = () => {
        const tag = tagInput.trim();
        if (tag && !tags.includes(tag)) {
            setTags((prev) => [...prev, tag]);
            setTagInput('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setTags((prev) => prev.filter((t) => t !== tagToRemove));
    };

    const handleTagKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddTag();
        }
    };

    const validateStep1 = (): boolean => {
        if (!name.trim() || !key.trim()) {
            setErrors({
                ...(!name.trim() ? { name: 'Name is required' } : {}),
                ...(!key.trim() ? { key: 'Key is required' } : {}),
            });
            return false;
        }
        setErrors({});
        return true;
    };

    const goNext = () => {
        if (currentStep === 0 && !validateStep1()) return;
        if (currentStep < TOTAL_STEPS - 1) setCurrentStep((s) => s + 1);
    };

    const goBack = () => {
        if (currentStep > 0) setCurrentStep((s) => s - 1);
    };

    const handleSubmit = async () => {
        if (!name.trim() || !key.trim()) {
            setErrors({
                ...(!name.trim() ? { name: 'Name is required' } : {}),
                ...(!key.trim() ? { key: 'Key is required' } : {}),
            });
            setCurrentStep(0);
            return;
        }

        try {
            const payload: Record<string, unknown> = {
                name: key.trim(),
                type: 'prompt',
                description: description.trim() || null,
                content: content.trim() || null,
                tags: tags.length > 0 ? tags : null,
                visibility,
            };

            const result = await createCapability.mutateAsync(payload);

            toast.success('Prompt created.');
            if (onSuccess && result && typeof result === 'object' && 'name' in result) {
                onSuccess(result as CreatedCapability);
            }
            onClose();
        } catch (error) {
            const message =
                error instanceof Error ? error.message : 'Unable to create prompt.';
            toast.error(message);
        }
    };

    const isLastStep = currentStep === TOTAL_STEPS - 1;
    const canGoNext =
        currentStep === 0 ? name.trim().length > 0 && key.trim().length > 0 : true;

    const inputClassName =
        'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500';
    const errorInputClassName =
        'w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

    return (
        <>
            {/* Header */}
            <div className="relative border-b border-slate-100 bg-slate-50/50 px-8 py-6">
                <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${colors.bgLight}`}>
                            <TypeIcon className={`h-5 w-5 ${colors.text}`} />
                        </div>
                        <div>
                            <h2 className="text-2xl font-semibold text-slate-900">
                                Add New Prompt
                            </h2>
                            <p className="text-sm text-slate-500">
                                Step {currentStep + 1} of {TOTAL_STEPS} · {STEP_META[currentStep].description}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full p-1 text-slate-400 outline-none transition-colors hover:bg-slate-200 hover:text-slate-600 focus:ring-2 focus:ring-slate-900"
                        aria-label="Close"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="mt-3 flex gap-2">
                    {STEP_META.map((step, i) => (
                        <div
                            key={step.title}
                            className={`h-1.5 flex-1 rounded-full ${i <= currentStep ? 'bg-indigo-500' : 'bg-slate-200'}`}
                            aria-hidden
                        />
                    ))}
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto bg-white p-8">
                <div className="mx-auto max-w-2xl space-y-6">
                    {currentStep === 0 && (
                        <>
                            <div className="space-y-2">
                                <Label>
                                    Prompt Name <span className="text-rose-500">*</span>
                                </Label>
                                <Input
                                    placeholder="e.g. system-prompt-analyzer"
                                    value={name}
                                    onChange={(e) => handleNameChange(e.target.value)}
                                    className={errors.name ? errorInputClassName : inputClassName}
                                    autoFocus
                                />
                                {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                            </div>

                            <div className="grid grid-cols-[1fr_auto] gap-4 items-start">
                                <div className="space-y-2">
                                    <Label>
                                        Key <span className="text-rose-500">*</span>
                                    </Label>
                                    <Input
                                        placeholder="e.g. system_prompt_analyzer"
                                        value={key}
                                        onChange={(e) => setKey(slugifyToUnderscore(e.target.value))}
                                        className={`font-mono text-xs ${errors.key ? errorInputClassName : inputClassName}`}
                                    />
                                    <p className="text-xs text-slate-400">
                                        Unique identifier for the prompt. Auto-generated from name.
                                    </p>
                                    {errors.key && <p className="text-xs text-red-500">{errors.key}</p>}
                                </div>

                                <VisibilitySelect
                                    value={visibility}
                                    onChange={setVisibility}
                                    className={inputClassName}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Description</Label>
                                <textarea
                                    placeholder="Brief description of what this prompt does..."
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    rows={2}
                                    className={`${inputClassName} resize-none`}
                                />
                            </div>

                            <div className="space-y-2">
                                <Label>Tags</Label>
                                <div className="flex gap-2">
                                    <Input
                                        placeholder="Add a tag..."
                                        value={tagInput}
                                        onChange={(e) => setTagInput(e.target.value)}
                                        onKeyDown={handleTagKeyDown}
                                        className={inputClassName}
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddTag}
                                        disabled={!tagInput.trim()}
                                        className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        <Plus size={14} />
                                    </button>
                                </div>
                                {tags.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {tags.map((tag) => (
                                            <span
                                                key={tag}
                                                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${colors.bgLight} ${colors.text}`}
                                            >
                                                {tag}
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveTag(tag)}
                                                    className="hover:opacity-70"
                                                    aria-label={`Remove tag ${tag}`}
                                                >
                                                    <XCircle size={14} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {currentStep === 1 && (
                        <div className="space-y-2">
                            <Label>Prompt text for the agent</Label>
                            <textarea
                                placeholder="Enter the prompt content that will be sent to the agent..."
                                value={content}
                                onChange={(e) => setContent(e.target.value)}
                                rows={14}
                                className={`${inputClassName} resize-none font-mono text-xs`}
                                autoFocus
                            />
                            <p className="text-xs text-slate-400">
                                You can use variables like {'{{variable_name}}'} in your prompt.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-3 border-t border-slate-100 bg-slate-50/50 px-8 py-6">
                <div>
                    {currentStep > 0 && (
                        <button
                            type="button"
                            onClick={goBack}
                            disabled={createCapability.isPending}
                            className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg disabled:opacity-50"
                        >
                            <ChevronLeft size={16} />
                            Back
                        </button>
                    )}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={createCapability.isPending}
                        className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-md transition-all hover:bg-slate-50 hover:shadow-lg disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    {isLastStep ? (
                        <button
                            type="button"
                            onClick={() => void handleSubmit()}
                            disabled={createCapability.isPending}
                            className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-btn-primary-hover hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {createCapability.isPending ? (
                                <>
                                    <Loader2 size={16} className="animate-spin" />
                                    Creating...
                                </>
                            ) : (
                                'Create Prompt'
                            )}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={goNext}
                            disabled={!canGoNext}
                            className="flex items-center gap-2 rounded-lg bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-md transition-all hover:bg-btn-primary-hover hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            Next
                            <ChevronRight size={16} />
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}
